import { Button, Flex } from 'antd';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAgentBackups } from '@modules/ai-agent/hooks/use-agent-backups';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import { Text } from '@shared/ui/antd';

import promptStyles from '../../prompt.module.scss';
import { mapAgentBackupToItem } from './lib/utils';
import styles from './backups.module.scss';
import { BackupsTable } from './backups-table';
import { BackupNameModal } from './ui/backup-name-modal/backup-name-modal';

interface BackupsSectionProps {
  agentId: number;
  onRestoreBackup: () => void | Promise<void>;
}

export const BackupsSection: FC<BackupsSectionProps> = ({ agentId, onRestoreBackup }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.backups' });
  const {
    backups,
    isLoading,
    isError,
    refetch,
    createBackup,
    renameBackup,
    restoreBackup,
    deleteBackup,
  } = useAgentBackups(agentId, onRestoreBackup);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [backupIdToRename, setBackupIdToRename] = useState<number | null>(null);
  const [backupIdToRestore, setBackupIdToRestore] = useState<number | null>(null);
  const [backupIdToDelete, setBackupIdToDelete] = useState<number | null>(null);
  const [isRestoreSubmitting, setIsRestoreSubmitting] = useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);
  const backupItems = useMemo(
    () => backups.map((backup) => mapAgentBackupToItem(backup, t('authorFallback'))),
    [backups, t],
  );

  const backupToRename = useMemo(
    () => backupItems.find((backup) => backup.id === backupIdToRename) ?? null,
    [backupIdToRename, backupItems],
  );
  const backupToRestore = useMemo(
    () => backupItems.find((backup) => backup.id === backupIdToRestore) ?? null,
    [backupIdToRestore, backupItems],
  );
  const backupToDelete = useMemo(
    () => backupItems.find((backup) => backup.id === backupIdToDelete) ?? null,
    [backupIdToDelete, backupItems],
  );

  const handleCreateBackup = async (title: string) => {
    await createBackup(title);
  };

  const handleRenameBackup = async (title: string) => {
    if (!backupToRename) return;

    await renameBackup(backupToRename.id, title);
  };

  const handleRestoreSelectedBackup = () => {
    if (!backupToRestore) return;

    void (async () => {
      try {
        setIsRestoreSubmitting(true);
        await restoreBackup(backupToRestore.id);
        setBackupIdToRestore(null);
      } catch {
        return;
      } finally {
        setIsRestoreSubmitting(false);
      }
    })();
  };

  const handleDeleteSelectedBackup = () => {
    if (!backupToDelete) return;

    void (async () => {
      try {
        setIsDeleteSubmitting(true);
        await deleteBackup(backupToDelete.id);
        setBackupIdToDelete(null);
      } catch {
        return;
      } finally {
        setIsDeleteSubmitting(false);
      }
    })();
  };

  return (
    <Flex vertical className={promptStyles.element} gap={16}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('title')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('description')}
        </Text>
      </Flex>

      {isLoading && <LoadingState />}
      {!isLoading && isError && <FetchingErrorState refetch={refetch} />}

      {!isLoading && !isError && (
        <>
          <div className={styles.tableWrapper}>
            <BackupsTable
              backups={backupItems}
              onRestoreBackup={setBackupIdToRestore}
              onRenameBackup={setBackupIdToRename}
              onDeleteBackup={setBackupIdToDelete}
            />
          </div>

          <Flex>
            <Button type="default" size="large" onClick={() => setIsCreateModalOpen(true)}>
              {t('createButton')}
            </Button>
          </Flex>
        </>
      )}

      <BackupNameModal
        isOpen={isCreateModalOpen}
        title={t('createModal.title')}
        placeholder={t('createModal.placeholder')}
        submitLabel={t('createModal.submit')}
        cancelLabel={t('createModal.cancel')}
        onSubmit={handleCreateBackup}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <BackupNameModal
        isOpen={Boolean(backupToRename)}
        title={t('renameModal.title')}
        placeholder={t('renameModal.placeholder')}
        initialValue={backupToRename?.title}
        submitLabel={t('renameModal.submit')}
        cancelLabel={t('renameModal.cancel')}
        onSubmit={handleRenameBackup}
        onClose={() => setBackupIdToRename(null)}
      />

      <ConfirmModal
        isOpen={Boolean(backupToRestore)}
        title={t('restoreModal.title')}
        text={
          backupToRestore
            ? t('restoreModal.text', {
                backup: backupToRestore.title,
              })
            : ''
        }
        confirmButtonLabel={t('restoreModal.confirm')}
        cancelButtonLabel={t('restoreModal.cancel')}
        isLoading={isRestoreSubmitting}
        onConfirm={handleRestoreSelectedBackup}
        onCancelButton={() => setBackupIdToRestore(null)}
        onClose={() => setBackupIdToRestore(null)}
      />

      <ConfirmModal
        isOpen={Boolean(backupToDelete)}
        title={t('deleteModal.title')}
        text={
          backupToDelete
            ? t('deleteModal.text', {
                backup: backupToDelete.title,
              })
            : ''
        }
        confirmButtonLabel={t('deleteModal.confirm')}
        cancelButtonLabel={t('deleteModal.cancel')}
        confirmButtonDanger
        isLoading={isDeleteSubmitting}
        onConfirm={handleDeleteSelectedBackup}
        onCancelButton={() => setBackupIdToDelete(null)}
        onClose={() => setBackupIdToDelete(null)}
      />
    </Flex>
  );
};
